/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Col,
  Loader,
  Row,
  SVGIcon,
  Tab,
  Tabs,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  CloudProviderType,
  useAccountSpendsQuery,
  useAccountV2Query,
} from "~/generated/graphql";
import { BreadcrumbsBox, Details, IconBox, UniversalWrap } from "~/components";
import { ErrorMessage } from "~/components/Error";
import { useUuidValidator } from "~/hooks";
import { useBillingPeriod, useCustomers } from "~/services/customers";
import { isNotNil, noop } from "~/tools";
import { ClusterUtilization } from "../components/ClusterUtilization";
import DownloadDescriptorButton from "../DownloadDescriptorButton";
import { getAccountOptions, isAccountDiscovered } from "../utils";
import { AccountLinks } from "./AccountLinks";
import { AccountSpends } from "./AccountSpends";
import { EstateRecords } from "./Tabs";
import {
  ActivityIcon,
  getDownloadButtonProps,
  getIcon,
  getIdDetail,
  getProvider,
  getProviderType,
  getStatus,
  isVMWare,
  showDownloadDescriptorButton,
} from "./utils";

type RouteParams = {
  accountId: string;
};

export function AccountDetailsPage() {
  const { accountId } = useParams<RouteParams>();
  const { isAdminLite } = useCustomers();
  useUuidValidator(accountId);
  const { isCurrentBillingPeriod } = useBillingPeriod();

  const { data, loading, error } = useAccountV2Query({
    variables: { id: accountId },
    fetchPolicy: "no-cache",
  });

  const providerId = data?.accountV2?.providerId ?? "";

  const { data: accountSpendsData } = useAccountSpendsQuery({
    variables: { accountProviderId: providerId },
    fetchPolicy: "no-cache",
  });

  const tags = [
    {
      color: "success",
      text: "Account",
    },
  ];

  const displayName =
    data?.accountV2?.displayName ?? data?.accountV2?.name ?? accountId;

  const provider = getProvider(data?.accountV2);
  const providerType = getProviderType(data?.accountV2);
  const detailsBase = [
    {
      label: "Account Name",
      value: displayName,
      url: null,
      showFragment: true,
      provider: data?.accountV2?.provider,
      fragment: ActivityIcon({
        provider: data?.accountV2?.provider,
        providerType: data?.accountV2?.providerType ?? undefined,
        activated: data?.accountV2?.activated,
        status: getStatus(data?.accountV2),
      }),
    },
    ...getIdDetail(
      data?.accountV2,
      getAccountOptions(provider ?? "", providerType)?.idColumn,
      provider
    ),
    isAdminLite
      ? undefined
      : {
          label: "Owner",
          value: data?.accountV2?.owner?.name,
          url: null,
        },
    isAdminLite
      ? undefined
      : {
          label: "Description",
          value: data?.accountV2?.description,
          url: null,
        },
  ].filter(isNotNil);

  const accountSpends = accountSpendsData?.accountSpends;
  const hasSpends = accountSpends != null;

  const breadcrumbLabels = data?.accountV2?.id
    ? [{ value: accountId, label: displayName }]
    : undefined;

  const showClusterUtilization =
    getProviderType(data?.accountV2) === CloudProviderType.Kubernetes ||
    getProviderType(data?.accountV2) === CloudProviderType.Openshift;

  return (
    <>
      <Row>
        <Col>
          {error && <ErrorMessage error={error} />}
          <BreadcrumbsBox
            title={displayName}
            tags={tags}
            labels={breadcrumbLabels}
          >
            {isVMWare(data?.accountV2) && (
              <Button
                icon="externalLink"
                css={{ marginRight: theme.spacing.spacing01 }}
                linkTo="https://docs.mca.nordcloudapp.com/docs/visibility/cloud-accounts/vmware/add-vmware"
              >
                Instructions
              </Button>
            )}
            <When condition={showDownloadDescriptorButton(data?.accountV2)}>
              <DownloadDescriptorButton
                {...getDownloadButtonProps(data?.accountV2)}
              />
            </When>
            <AccountLinks
              accountId={accountId}
              isSettingsLinkVisible={isCurrentBillingPeriod}
              isOnBoardLinkVisible={
                isAccountDiscovered(providerType, getStatus(data?.accountV2)) &&
                isCurrentBillingPeriod
              }
            />
          </BreadcrumbsBox>
          <Box spacing="spacing04">
            <UniversalWrap
              errorProps={{ error }}
              loaderProps={{
                loading,
                Component: Loader,
              }}
            >
              <Row>
                <Col xs={12} md={1}>
                  <IconBox
                    icon={
                      <SVGIcon name={getIcon(data?.accountV2)} size="xxl" />
                    }
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Text weight="medium">Account Details</Text>
                  <Details details={detailsBase} />
                </Col>
                <When condition={hasSpends}>
                  <Col xs={12} md={3}>
                    <AccountSpends
                      currentMonthValue={accountSpends?.currentMonthValue ?? 0}
                      currentMonthForecastValue={
                        accountSpends?.currentMonthForecastValue ?? 0
                      }
                      previousMonthValue={
                        accountSpends?.previousMonthValue ?? 0
                      }
                    />
                  </Col>
                </When>
              </Row>
            </UniversalWrap>
          </Box>
        </Col>
      </Row>
      <When condition={showClusterUtilization}>
        <ClusterUtilization nordcloudId={accountId} />
      </When>
      <Row>
        <Col>
          <Tabs handleTab={noop} step={0}>
            <Tab heading="Estate Records" caption="Account estate records">
              <EstateRecords
                providerId={providerId}
                providerType={data?.accountV2?.providerType ?? ""}
              />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    </>
  );
}
